import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { 
  MapPin, 
  Clock, 
  Users, 
  Car, 
  Navigation, 
  Plus, 
  X,
  CheckCircle,
  AlertTriangle,
  User,
  Mail,
  Phone,
  Lock,
  CreditCard,
  Loader
} from 'lucide-react';
import { pricingService } from '../services/pricingService';
import { googleMapsService } from '../services/googleMapsService';
import AddressInput from './AddressInput';
import DateTimePicker from './DateTimePicker';
import FareBreakdown from './FareBreakdown';
import TravelInfo from './TravelInfo';
import GoogleMap from './GoogleMap';
import PaymentForm from './PaymentForm';
import PaymentMethodSelector from './PaymentMethodSelector';
import type { FareBreakdown as FareBreakdownType } from '../types/pricing';
import type { TravelInfo as TravelInfoType } from '../services/googleMapsService';
import type { AddressDetails } from '../services/googlePlacesService';
import { scrollToActionZone, scrollToFormError } from '../utils/scrollUtils';
import { guestBookingService, type GuestBookingData } from '../services/guestBookingService';

interface Stop {
  id: string;
  address: string;
  details: AddressDetails | null;
}

interface GuestInfo {
  name: string;
  email: string;
  phone: string;
  password?: string;
  needsPassword?: boolean;
}

const GuestBookingForm: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<'booking' | 'guest-info' | 'password' | 'confirmation'>('booking');
  const [pickup, setPickup] = useState('');
  const [dropoff, setDropoff] = useState('');
  const [pickupDetails, setPickupDetails] = useState<AddressDetails | null>(null);
  const [dropoffDetails, setDropoffDetails] = useState<AddressDetails | null>(null);
  const [stops, setStops] = useState<Stop[]>([]);
  const [pickupTime, setPickupTime] = useState<Date>(pricingService.getMinimumPickupTime());
  const [pickupTimeError, setPickupTimeError] = useState<string | null>(null);
  const [vehicleFeatures, setVehicleFeatures] = useState<string[]>([]);
  const [supportWorkers, setSupportWorkers] = useState(0);
  const [specialRequirements, setSpecialRequirements] = useState('');
  const [travelInfo, setTravelInfo] = useState<TravelInfoType | null>(null);
  const [isCalculatingRoute, setIsCalculatingRoute] = useState(false);
  const [fareBreakdown, setFareBreakdown] = useState<FareBreakdownType | null>(null);
  const [isCalculatingFare, setIsCalculatingFare] = useState(false);
  const [guestInfo, setGuestInfo] = useState<GuestInfo>({
    name: '',
    email: '',
    phone: '',
    password: undefined,
    needsPassword: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [bookingResult, setBookingResult] = useState<{ 
    booking_id: string; 
    access_token: string; 
    reference: string;
    payment_method: 'cash_bank' | 'stripe';
  } | null>(null);
  const [wantsAccount, setWantsAccount] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<'cash_bank' | 'stripe'>('cash_bank');
  /** Card flow: unpaid row created in `bookings` before PaymentIntent */
  const [stripeBookingId, setStripeBookingId] = useState<string | null>(null);
  const [stripeBookingError, setStripeBookingError] = useState<string | null>(null);
  const [stripeBookingLoading, setStripeBookingLoading] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (pickupDetails && dropoffDetails && areAllStopsValid()) {
      calculateTravelInfo();
    } else {
      setTravelInfo(null);
    }
  }, [pickupDetails, dropoffDetails, stops]);

  useEffect(() => {
    calculateFare();
  }, [vehicleFeatures, supportWorkers, travelInfo, pickupTime]);

  const areAllStopsValid = (): boolean => {
    return stops.every(stop => stop.details !== null);
  };

  const addStop = () => {
    if (stops.length >= 2) return;
    
    const newStop: Stop = {
      id: `stop-${Date.now()}`,
      address: '',
      details: null
    };
    
    setStops(prev => [...prev, newStop]);
  };

  const removeStop = (stopId: string) => {
    setStops(prev => prev.filter(stop => stop.id !== stopId));
  };

  const updateStop = (stopId: string, address: string, details?: AddressDetails) => {
    setStops(prev => prev.map(stop => 
      stop.id === stopId 
        ? { ...stop, address, details: details || null }
        : stop
    ));
  };

  const calculateTravelInfo = async () => {
    if (!pickupDetails || !dropoffDetails || !areAllStopsValid()) return;

    setIsCalculatingRoute(true);
    
    try {
      const waypoints = [
        { lat: pickupDetails.latitude, lng: pickupDetails.longitude },
        ...stops.filter(stop => stop.details).map(stop => ({
          lat: stop.details!.latitude,
          lng: stop.details!.longitude
        })),
        { lat: dropoffDetails.latitude, lng: dropoffDetails.longitude }
      ];
      
      const result = await googleMapsService.getMultiStopRoute(waypoints);
      setTravelInfo(result);
    } catch (error) {
      console.error('Error calculating travel info:', error);
      setTravelInfo({ 
        distance: { text: 'Unknown', miles: 0 }, 
        duration: { text: 'Unknown', minutes: 0 }, 
        status: 'ERROR' 
      });
    } finally {
      setIsCalculatingRoute(false);
    }
  };

  const calculateFare = async () => {
    if (!travelInfo || travelInfo.status !== 'OK') {
      setFareBreakdown(null);
      return;
    }

    setIsCalculatingFare(true);

    try {
      await new Promise(resolve => setTimeout(resolve, 300));

      const breakdown = pricingService.calculateEstimatedFare(
        vehicleFeatures,
        supportWorkers,
        travelInfo.distance.miles,
        travelInfo.duration.minutes,
        new Date(),
        pickupTime
      );

      setFareBreakdown(breakdown);
    } catch (error) {
      console.error('Error calculating fare:', error);
      setFareBreakdown(null);
    } finally {
      setIsCalculatingFare(false);
    }
  };

  const handleFeatureToggle = (feature: string) => {
    setVehicleFeatures(prev => 
      prev.includes(feature) 
        ? prev.filter(f => f !== feature)
        : [...prev, feature]
    );
  };

  const handlePickupChange = (value: string, details?: AddressDetails) => {
    setPickup(value);
    setPickupDetails(details || null);
  };

  const handleDropoffChange = (value: string, details?: AddressDetails) => {
    setDropoff(value);
    setDropoffDetails(details || null);
  };

  const handleGuestInfoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setGuestInfo(prev => ({ ...prev, [name]: value }));
  };

  const canProceedToGuestInfo = () => {
    return !!(
      pickup && dropoff && 
      pickupDetails && dropoffDetails && 
      areAllStopsValid() && 
      fareBreakdown && 
      !pickupTimeError
    );
  };

  const canSubmitBooking = () => {
    return !!(
      guestInfo.name.trim() && 
      guestInfo.email.trim() && 
      guestInfo.phone.trim() &&
      (!wantsAccount || guestInfo.password?.trim()) &&
      canProceedToGuestInfo()
    );
  };

  const buildGuestBookingPayload = useCallback(
    (paymentMethod: 'cash_bank' | 'stripe'): GuestBookingData | null => {
      if (!fareBreakdown || !pickupDetails || !dropoffDetails) return null;

      return {
        guestName: guestInfo.name.trim(),
        guestEmail: guestInfo.email.trim(),
        guestPhone: guestInfo.phone.trim(),
        createAccount: wantsAccount,
        password: wantsAccount ? guestInfo.password : undefined,
        pickup: pickupDetails,
        dropoff: dropoffDetails,
        stops: stops.filter((stop) => stop.details).map((stop) => stop.details!),
        pickupTime: pickupTime.toISOString(),
        dropoffTime: null,
        vehicleFeatures,
        supportWorkersCount: supportWorkers,
        fareEstimate: fareBreakdown.estimatedTotal,
        bookingType: fareBreakdown.bookingType.type,
        leadTimeHours: fareBreakdown.bookingType.leadTimeHours || 0,
        timeMultiplier: fareBreakdown.bookingType.multiplier || 1.0,
        bookingTypeDiscount: fareBreakdown.bookingType.discount || 0,
        specialRequirements: specialRequirements || null,
        notes: null,
        paymentMethod,
      };
    },
    [
      fareBreakdown,
      pickupDetails,
      dropoffDetails,
      guestInfo.name,
      guestInfo.email,
      guestInfo.phone,
      guestInfo.password,
      stops,
      pickupTime,
      vehicleFeatures,
      supportWorkers,
      specialRequirements,
      wantsAccount,
    ]
  );

  useEffect(() => {
    if (!showPayment) {
      setStripeBookingId(null);
      setStripeBookingError(null);
      setStripeBookingLoading(false);
      return;
    }

    if (paymentMethod !== 'stripe') {
      setStripeBookingId(null);
      setStripeBookingError(null);
      setStripeBookingLoading(false);
      return;
    }

    if (stripeBookingId) {
      return;
    }

    setStripeBookingLoading(true);

    let cancelled = false;

    async function reserveForCard() {
      setStripeBookingError(null);

      const payload = buildGuestBookingPayload('stripe');
      if (!payload) {
        if (!cancelled) {
          setStripeBookingError('Missing journey or fare details.');
          setStripeBookingLoading(false);
        }
        return;
      }

      try {
        const result = await guestBookingService.createGuestBooking(payload);
        if (cancelled) return;

        if (result.success && result.booking_id) {
          setStripeBookingId(result.booking_id);
        } else {
          setStripeBookingId(null);
          setStripeBookingError(result.message ?? 'Could not create booking.');
        }
      } catch (e) {
        if (!cancelled) {
          setStripeBookingError(e instanceof Error ? e.message : 'Could not create booking.');
        }
      } finally {
        if (!cancelled) setStripeBookingLoading(false);
      }
    }

    void reserveForCard();

    return () => {
      cancelled = true;
    };
  }, [showPayment, paymentMethod, stripeBookingId, buildGuestBookingPayload]);

  const handleProceedToGuestInfo = () => {
    if (canProceedToGuestInfo()) {
      setCurrentStep('guest-info');
      scrollToActionZone('.form-step.active');
      
      // Animate step transition
      if (containerRef.current) {
        gsap.fromTo(containerRef.current,
          { opacity: 0, x: 20 },
          { opacity: 1, x: 0, duration: 0.5, ease: "power2.out" }
        );
      }
    }
  };

  const handleSubmitGuestInfo = async () => {
    if (!guestInfo.name.trim() || !guestInfo.email.trim() || !guestInfo.phone.trim()) {
      setSubmitError('Please add your name, email, and phone.');
      scrollToFormError();
      return;
    }

    if (wantsAccount && !guestInfo.password?.trim()) {
      setSubmitError('Please enter a password to create your account.');
      scrollToFormError();
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    setPaymentError(null);
    setShowPayment(false);
    setStripeBookingId(null);
    setStripeBookingError(null);

    try {
      // Show payment form instead of creating booking immediately
      setShowPayment(true);
      scrollToActionZone('.payment-section');

    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Booking failed');
      scrollToFormError();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePaymentSuccess = async (paymentIntent: { id: string; metadata?: { booking_id?: string } }) => {
    try {
      console.log('Payment successful, booking already created');

      const bookingId =
        (paymentIntent.metadata?.booking_id as string | undefined) || stripeBookingId || '';

      // The booking was created during payment process
      setBookingResult({
        booking_id: bookingId,
        access_token: '',
        reference:
          bookingId.replace(/-/g, '').slice(0, 8).toUpperCase() || paymentIntent.id,
        payment_method: 'stripe'
      });
      
      setCurrentStep('confirmation');
      setShowPayment(false);
      scrollToActionZone('.confirmation');

      // Animate to confirmation
      if (containerRef.current) {
        gsap.fromTo(containerRef.current,
          { opacity: 0, scale: 0.95 },
          { opacity: 1, scale: 1, duration: 0.6, ease: "back.out(1.7)" }
        );
      }

    } catch (error) {
      console.error('Post-payment processing error:', error);
      setPaymentError(error instanceof Error ? error.message : 'Booking creation failed');
    }
  };

  const handlePaymentError = (error: string) => {
    setPaymentError(error);
    scrollToActionZone('.payment-error');
  };

  const handleCashBankBooking = async () => {
    setIsSubmitting(true);
    setSubmitError(null);
    setPaymentError(null);

    try {
      console.log('🚀 Starting cash/bank booking submission...', {
        guestName: guestInfo.name,
        guestEmail: guestInfo.email,
        guestPhone: guestInfo.phone,
        pickup: pickupDetails?.formattedAddress,
        dropoff: dropoffDetails?.formattedAddress,
        pickupTime: pickupTime.toISOString(),
        fareEstimate: fareBreakdown?.estimatedTotal,
        paymentMethod: 'cash_bank'
      });

      const bookingData = buildGuestBookingPayload('cash_bank');
      if (!bookingData) {
        const msg = 'Missing booking details. Please check locations and fare.';
        setSubmitError(msg);
        setPaymentError(msg);
        return;
      }

      console.log('📋 Booking data prepared:', bookingData);
      const result = await guestBookingService.createGuestBooking(bookingData);
      
      console.log('📥 Guest booking service response:', result);
      
      if (result.success && result.booking_id) {
        console.log('✅ Booking created successfully:', result);
        setPaymentError(null);
        setSubmitError(null);
        setBookingResult({
          booking_id: result.booking_id,
          access_token: result.access_token,
          reference: result.reference ?? result.booking_id,
          payment_method: 'cash_bank'
        });
        
        // Handle account creation result
        if (result.user_account) {
          if (result.user_account.success) {
            console.log('✅ User account created/linked successfully:', result.user_account);
          } else {
            console.warn('⚠️ User account creation failed:', result.user_account.error);
            // Don't fail the booking for account creation issues
          }
        }
        
        setCurrentStep('confirmation');
        setShowPayment(false);
        scrollToActionZone('.confirmation');

        // Animate to confirmation
        if (containerRef.current) {
          gsap.fromTo(containerRef.current,
            { opacity: 0, scale: 0.95 },
            { opacity: 1, scale: 1, duration: 0.6, ease: "back.out(1.7)" }
          );
        }
      } else {
        console.error('❌ Booking creation failed:', result);
        const msg = result.message || 'Booking creation failed';
        setPaymentError(msg);
        throw new Error(msg);
      }

    } catch (error) {
      console.error('❌ Cash/Bank booking error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Booking creation failed';
      const full = `Unable to confirm your booking: ${errorMessage}`;
      setSubmitError(full);
      setPaymentError(full);
      scrollToActionZone('.payment-error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const availableFeatures = pricingService.getVehicleFeatures();
  const supportWorkerTiers = pricingService.getSupportWorkerTiers();

  return (
    <div ref={containerRef} className="max-w-7xl mx-auto">
      {/* Payment Section */}
      {showPayment && fareBreakdown && (
        <div className="payment-section">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Payment</h3>

          {(paymentError || submitError) && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 payment-error">
              <div className="flex items-center text-red-700">
                <AlertTriangle className="w-5 h-5 mr-2 flex-shrink-0" />
                <span>{paymentError || submitError}</span>
              </div>
            </div>
          )}

          {/* Payment Method Selection */}
          <div className="mb-8">
            <PaymentMethodSelector
              selectedMethod={paymentMethod}
              onMethodChange={setPaymentMethod}
              amount={fareBreakdown.estimatedTotal}
            />
          </div>

          {/* Payment Form or Booking Confirmation */}
          {paymentMethod === 'stripe' ? (
            <div className="grid lg:grid-cols-2 gap-8">
              <div>
                {stripeBookingError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-start text-red-800 text-sm">
                    <AlertTriangle className="w-5 h-5 mr-2 flex-shrink-0" />
                    <span>{stripeBookingError}</span>
                  </div>
                )}
                {stripeBookingLoading ? (
                  <div className="flex flex-col items-center justify-center py-12 px-4 border border-gray-200 rounded-xl bg-gray-50">
                    <Loader className="w-8 h-8 text-blue-500 animate-spin mb-3" />
                    <p className="text-gray-600 text-center text-sm">Setting up checkout…</p>
                  </div>
                ) : stripeBookingId ? (
                  <PaymentForm
                    bookingId={stripeBookingId}
                    amount={fareBreakdown.estimatedTotal}
                    breakdown={{
                      baseFare: fareBreakdown.baseFare,
                      distanceFare: fareBreakdown.distance.totalCost,
                      vehicleFeatures: fareBreakdown.vehicleFeatures.reduce((sum, f) => sum + f.price, 0),
                      supportWorkerCost: fareBreakdown.supportWorkers.totalCost,
                      peakTimeSurcharge: fareBreakdown.timeOfDay.surcharge || 0,
                      bookingTypeDiscount: fareBreakdown.bookingType.type === 'on_demand' ? 
                        (fareBreakdown.baseFare + fareBreakdown.distance.totalCost + fareBreakdown.vehicleFeatures.reduce((sum, f) => sum + f.price, 0) + fareBreakdown.supportWorkers.totalCost) * 0.5 :
                        fareBreakdown.bookingType.discount || 0
                    }}
                    bookingData={{
                      pickup_address: pickup,
                      dropoff_address: dropoff,
                      pickup_time: pickupTime.toISOString(),
                      dropoff_time: null,
                      vehicle_features: vehicleFeatures,
                      support_workers_count: supportWorkers,
                      fare_estimate: fareBreakdown.estimatedTotal,
                      booking_type: fareBreakdown?.bookingType.type || 'scheduled',
                      lead_time_hours: fareBreakdown?.bookingType.leadTimeHours || 0,
                      time_multiplier: fareBreakdown?.bookingType.multiplier || 1.0,
                      booking_type_discount: fareBreakdown?.bookingType.discount || 0,
                      special_requirements: specialRequirements || null
                    }}
                    guestInfo={{
                      name: guestInfo.name,
                      email: guestInfo.email,
                      phone: guestInfo.phone
                    }}
                    onSuccess={handlePaymentSuccess}
                    onError={handlePaymentError}
                  />
                ) : (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-center text-sm text-amber-900">
                    Fix the issue above or go back and try again — we couldn’t prepare card checkout.
                  </div>
                )}
              </div>
              
              <div>
                <FareBreakdown
                  breakdown={fareBreakdown}
                  isCalculating={false}
                  showEstimated={true}
                />
              </div>
            </div>
          ) : (
            /* Cash/Bank Payment Confirmation */
            <div className="grid lg:grid-cols-2 gap-8">
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-5">
                <h4 className="font-semibold text-gray-900 mb-2">Cash or bank transfer</h4>
                <p className="text-sm text-gray-600 mb-4">
                  We’ll email you how to pay. Have the fare ready for the driver if paying cash.
                </p>
                <button
                  onClick={handleCashBankBooking}
                  disabled={isSubmitting}
                  className="w-full py-3 px-4 rounded-lg font-semibold bg-gray-900 text-white hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Saving…' : 'Confirm booking'}
                </button>
              </div>

              <div>
                <FareBreakdown
                  breakdown={fareBreakdown}
                  isCalculating={false}
                  showEstimated={true}
                />
              </div>
            </div>
          )}

          <div className="flex justify-between mt-8">
            <button
              onClick={() => setShowPayment(false)}
              className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              Back
            </button>
          </div>
        </div>
      )}

      {currentStep === 'booking' && !showPayment && (
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-10">
          {/* Booking Form */}
          <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 border border-gray-100 form-step active guest-booking-form">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
              <Car className="w-5 h-5 text-blue-600" />
              Your trip
            </h3>

            {/* Location Inputs */}
            <div className="space-y-6 mb-8">
              <AddressInput
                label="Pickup Location"
                placeholder="Enter pickup address"
                value={pickup}
                onChange={handlePickupChange}
                onLocationSelect={setPickupDetails}
                showUseLocation={true}
                icon="pickup"
              />

              {/* Stops */}
              <div className="space-y-4">
                {stops.map((stop, index) => (
                  <div key={stop.id} className="relative">
                    <div className="flex items-start space-x-3">
                      <div className="flex-1">
                        <AddressInput
                          label={`Stop ${index + 1}`}
                          placeholder="Enter stop address"
                          value={stop.address}
                          onChange={(value, details) => updateStop(stop.id, value, details)}
                          onLocationSelect={(details) => updateStop(stop.id, stop.address, details)}
                         showUseLocation={true}
                          icon="pickup"
                        />
                      </div>
                      <div className="flex-shrink-0 mt-12">
                        <button
                          onClick={() => removeStop(stop.id)}
                          className="w-6 h-6 bg-red-100 text-red-600 rounded-lg flex items-center justify-center hover:bg-red-200 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {stops.length < 2 && (
                <div className="flex justify-center">
                  <button
                    onClick={addStop}
                    className="flex items-center px-4 py-3 bg-blue-50 text-blue-600 rounded-xl border-2 border-blue-200 border-dashed hover:bg-blue-100 hover:border-blue-300 transition-all duration-300"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    Add Stop ({stops.length}/2)
                  </button>
                </div>
              )}

              <AddressInput
                label="Final Drop-off Location"
                placeholder="Enter destination address"
                value={dropoff}
                onChange={handleDropoffChange}
                onLocationSelect={setDropoffDetails}
               showUseLocation={true}
                icon="dropoff"
              />
            </div>

            {/* Pickup Time */}
            <div className="mb-8">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Pickup date & time *
              </label>
              <p className="text-xs text-gray-500 mb-3">Advance bookings may qualify for a discount (shown in your fare).</p>
              <DateTimePicker
                value={pickupTime}
                onChange={(date: Date) => {
                  setPickupTime(date);
                  setPickupTimeError(null);
                }}
                min={pricingService.getMinimumPickupTime()}
                max={pricingService.getMaximumPickupTime()}
                label=""
                className="w-full"
              />
              
              <div className="mt-2 min-h-[1.25rem]">
                {pickupTimeError ? (
                  <p className="text-sm text-red-600 flex items-center">
                    <AlertTriangle className="w-4 h-4 mr-1" />
                    {pickupTimeError}
                  </p>
                ) : (
                  <p className="text-xs text-gray-500">
                    Earliest pickup {pricingService.getMinimumPickupTime().toLocaleString()}
                  </p>
                )}
              </div>
            </div>

            {/* Travel Information */}
            <div className="mb-8">
              <TravelInfo 
                travelInfo={travelInfo}
                isLoading={isCalculatingRoute}
                stopsCount={stops.length}
              />
            </div>

            {/* Vehicle Features */}
            <div className="mb-8">
              <p className="text-sm text-gray-600 mb-4">
                Standard ride includes wide-door access at no extra cost.
              </p>

              <label className="block text-sm font-medium text-gray-700 mb-3">
                Add-ons (optional)
              </label>
              <div className="grid grid-cols-1 gap-3">
                {availableFeatures.map((feature) => {
                  const isSelected = vehicleFeatures.includes(feature.id);
                  const IconComponent = feature.icon === 'wheelchair' ? Car : 
                                      feature.icon === 'door' ? Car :
                                      feature.icon === 'lift' ? Users :
                                      feature.icon === 'oxygen' ? Car : Car;
                  
                  return (
                    <label key={feature.id} className={`flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all duration-300 ${
                      isSelected 
                        ? 'border-blue-500 bg-blue-50 shadow-md' 
                        : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/50'
                    }`}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleFeatureToggle(feature.id)}
                        className="w-5 h-5 text-blue-600 border-2 border-gray-300 rounded focus:ring-blue-500 focus:ring-offset-2"
                      />
                      <IconComponent className={`w-5 h-5 mx-3 ${isSelected ? 'text-blue-600' : 'text-gray-600'}`} />
                      <div className="flex-1">
                        <span className={`font-medium ${isSelected ? 'text-blue-800' : 'text-gray-700'}`}>
                          {feature.name}
                        </span>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm text-gray-500 line-clamp-2">{feature.description}</span>
                          <span className={`text-sm font-semibold ${isSelected ? 'text-blue-600' : 'text-gray-600'}`}>
                            +{pricingService.formatCurrency(feature.price)}
                          </span>
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Support Workers */}
            <div className="mb-8">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Support workers (0–4)
              </label>
              <div className="space-y-3">
                {supportWorkerTiers.map((tier) => (
                  <label 
                    key={tier.count} 
                    className={`flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all duration-300 ${
                      supportWorkers === tier.count 
                        ? 'border-teal-500 bg-teal-50 shadow-md' 
                        : 'border-gray-200 hover:border-teal-300 hover:bg-teal-50/50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="supportWorkers"
                      checked={supportWorkers === tier.count}
                      onChange={() => setSupportWorkers(tier.count)}
                      className="w-5 h-5 text-teal-600 border-2 border-gray-300 focus:ring-teal-500 focus:ring-offset-2"
                    />
                    <div className="ml-4 flex-1">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          {tier.count === 0 ? (
                            <Car className={`w-5 h-5 mr-2 ${supportWorkers === tier.count ? 'text-teal-600' : 'text-gray-600'}`} />
                          ) : (
                            <Users className={`w-5 h-5 mr-2 ${supportWorkers === tier.count ? 'text-teal-600' : 'text-gray-600'}`} />
                          )}
                          <span className={`font-medium ${supportWorkers === tier.count ? 'text-teal-800' : 'text-gray-700'}`}>
                            {tier.count === 0 ? 'No Support Needed' : `${tier.count} Support Worker${tier.count > 1 ? 's' : ''}`}
                          </span>
                        </div>
                        <span className={`text-sm font-semibold ${supportWorkers === tier.count ? 'text-teal-600' : 'text-gray-600'}`}>
                          {tier.count === 0 ? 'Free' : `${pricingService.formatCurrency(tier.hourlyRate)}/hour`}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">{tier.description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Special Requirements */}
            <div className="mb-8">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Note or Special Request
              </label>
              <textarea
                value={specialRequirements}
                onChange={(e) => setSpecialRequirements(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                rows={3}
                placeholder="Optional — accessibility, luggage, pickup notes…"
              />
            </div>

            {/* Fare Breakdown */}
            <div className="mb-8">
              <FareBreakdown 
                breakdown={fareBreakdown}
                isCalculating={isCalculatingFare}
                showEstimated={true}
              />
            </div>

            {/* Continue Button */}
            <button
              onClick={handleProceedToGuestInfo}
              disabled={!canProceedToGuestInfo()}
              className={`w-full py-3.5 rounded-xl text-base font-semibold transition-shadow ${
                canProceedToGuestInfo()
                  ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-md'
                  : 'bg-gray-200 text-gray-500 cursor-not-allowed'
              }`}
            >
              <Navigation className="inline-block w-5 h-5 mr-2 align-middle" />
              {canProceedToGuestInfo()
                ? `Continue (${fareBreakdown ? pricingService.formatCurrency(fareBreakdown.estimatedTotal) : '—'})`
                : 'Complete trip details to continue'}
            </button>

            <p className="text-center text-xs text-gray-500 mt-3">No account needed to book.</p>
          </div>

          {/* Map */}
          <div className="bg-white dark:bg-dark-800/95 backdrop-blur-sm rounded-2xl shadow-lg p-5 border border-gray-100 dark:border-dark-700 h-full">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-50 mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              Route map
            </h3>
            
            <GoogleMap 
              pickup={pickup}
              dropoff={dropoff}
              pickupCoords={pickupDetails ? { lat: pickupDetails.latitude, lng: pickupDetails.longitude } : undefined}
              dropoffCoords={dropoffDetails ? { lat: dropoffDetails.latitude, lng: dropoffDetails.longitude } : undefined}
              stops={stops.filter(stop => stop.details).map(stop => ({
                address: stop.address,
                coords: { lat: stop.details!.latitude, lng: stop.details!.longitude }
              }))}
              showProviders={true}
              maxProviders={20}
              searchRadius={15}
              className="h-96"
            />

            <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
              {pickupDetails && dropoffDetails && areAllStopsValid()
                ? `${travelInfo?.distance.text ?? '—'} · Estimated journey`
                : 'Choose addresses from the suggestions to preview the route.'}
            </p>
          </div>
        </div>
      )}

      {currentStep === 'guest-info' && !showPayment && (
        <div className="max-w-lg mx-auto px-4">
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-lg p-6 md:p-8 border border-gray-100 dark:border-dark-700">
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50">Contact</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">For confirmation and pickup.</p>
            </div>

            {submitError && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
                <div className="flex items-center text-red-700 dark:text-red-400">
                  <AlertTriangle className="w-5 h-5 mr-2" />
                  <span>{submitError}</span>
                </div>
              </div>
            )}

            <form onSubmit={(e) => { e.preventDefault(); handleSubmitGuestInfo(); }} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Name *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    name="name"
                    value={guestInfo.name}
                    onChange={handleGuestInfoChange}
                    required
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 dark:border-dark-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-dark-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                    placeholder="Enter your full name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    name="email"
                    value={guestInfo.email}
                    onChange={handleGuestInfoChange}
                    required
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 dark:border-dark-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-dark-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                    placeholder="you@example.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Phone *
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="tel"
                    name="phone"
                    value={guestInfo.phone}
                    onChange={handleGuestInfoChange}
                    required
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 dark:border-dark-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-dark-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                    placeholder="Mobile or landline"
                  />
                </div>
              </div>

              {/* Optional Account Creation */}
              <div className="rounded-lg border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700/40 p-4">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="wantsAccount"
                    checked={wantsAccount}
                    onChange={(e) => setWantsAccount(e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded mt-1"
                  />
                  <div className="flex-1 min-w-0">
                    <label htmlFor="wantsAccount" className="text-sm font-medium text-gray-900 dark:text-gray-100 cursor-pointer">
                      Create an account (optional)
                    </label>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      Track future bookings — not required to complete this one.
                    </p>
                  </div>
                </div>
                
                {wantsAccount && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-blue-800 mb-2">
                      Create Password *
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-blue-400" />
                      <input
                        type="password"
                        value={guestInfo.password || ''}
                        onChange={(e) => setGuestInfo(prev => ({ ...prev, password: e.target.value }))}
                        required={wantsAccount}
                        minLength={6}
                        className="w-full pl-12 pr-4 py-3 border border-blue-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Create a secure password (min 6 characters)"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Booking Summary */}
              <div className="rounded-lg bg-gray-50 dark:bg-dark-700/30 p-4 border border-gray-200 dark:border-dark-600">
                <h4 className="font-medium text-gray-900 dark:text-gray-100 text-sm mb-3">Summary</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">From:</span>
                    <span className="text-gray-800 font-medium">{pickup}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">To:</span>
                    <span className="text-gray-800 font-medium">{dropoff}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Pickup:</span>
                    <span className="text-gray-800 font-medium">{pickupTime.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Support Workers:</span>
                    <span className="text-gray-800 font-medium">{supportWorkers === 0 ? 'None' : supportWorkers}</span>
                  </div>
                  <div className="flex justify-between border-t border-gray-300 pt-2 mt-2">
                    <span className="text-gray-900 font-semibold">Total Fare:</span>
                    <span className="text-blue-600 font-bold text-lg">
                      {fareBreakdown ? pricingService.formatCurrency(fareBreakdown.estimatedTotal) : ''}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setCurrentStep('booking')}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={!canSubmitBooking() || isSubmitting}
                  className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <span className="inline-flex items-center justify-center gap-2">
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Please wait…
                    </span>
                  ) : (
                    <>
                      <CreditCard className="inline-block w-4 h-4 mr-2 align-middle" />
                      Continue to payment
                    </>
                  )}
                </button>
              </div>
            </form>

            <p className="text-center text-xs text-gray-500 dark:text-gray-400 mt-5">
              Continuing accepts our terms and privacy notice.
            </p>
          </div>
        </div>
      )}

      {currentStep === 'confirmation' && (
        <div className="max-w-lg mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 border border-gray-100 text-center confirmation">
            {bookingResult ? (
              <>
                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
                  <CheckCircle className="w-7 h-7 text-green-600" />
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-2">Thank you — your booking is in</h3>
                <p className="text-gray-600 text-sm mb-6">
                  We&apos;ve sent a confirmation email with these trip details (and{' '}
                  {bookingResult.payment_method === 'cash_bank' ? (
                    <>
                      instructions for{' '}
                      <span className="font-medium text-gray-800">cash or bank transfer</span>
                    </>
                  ) : (
                    <>
                      your <span className="font-medium text-gray-800">card receipt</span> where applicable
                    </>
                  )}
                  ). Reference:&nbsp;
                  <span className="font-mono font-semibold text-gray-900">
                    {bookingResult.booking_id
                      ? bookingResult.booking_id.replace(/-/g, '').slice(0, 8).toUpperCase()
                      : '—'}
                  </span>
                </p>

                <div className="text-left rounded-lg border border-gray-200 bg-gray-50 p-4 mb-6 text-sm space-y-2">
                  <p>
                    <span className="text-gray-500">Passenger</span>
                    <span className="block font-medium text-gray-900">{guestInfo.name}</span>
                  </p>
                  <p>
                    <span className="text-gray-500">Email</span>
                    <span className="block font-medium text-gray-900">{guestInfo.email}</span>
                  </p>
                  <p>
                    <span className="text-gray-500">Phone</span>
                    <span className="block font-medium text-gray-900">{guestInfo.phone}</span>
                  </p>
                  <p>
                    <span className="text-gray-500">From</span>
                    <span className="block font-medium text-gray-900">{pickup}</span>
                  </p>
                  {stops.filter((s) => s.details).map((stop, idx) => (
                    <p key={stop.id}>
                      <span className="text-gray-500">Stop {idx + 1}</span>
                      <span className="block font-medium text-gray-900">{stop.address}</span>
                    </p>
                  ))}
                  <p>
                    <span className="text-gray-500">To</span>
                    <span className="block font-medium text-gray-900">{dropoff}</span>
                  </p>
                  <p>
                    <span className="text-gray-500">Pickup</span>
                    <span className="block font-medium text-gray-900">{pickupTime.toLocaleString()}</span>
                  </p>
                  <p>
                    <span className="text-gray-500">Add-ons</span>
                    <span className="block font-medium text-gray-900">
                      {vehicleFeatures.length
                        ? pricingService
                            .getVehicleFeatures()
                            .filter((f) => vehicleFeatures.includes(f.id))
                            .map((f) => f.name)
                            .join(' · ')
                        : 'None'}
                    </span>
                  </p>
                  <p>
                    <span className="text-gray-500">Support workers</span>
                    <span className="block font-medium text-gray-900">
                      {supportWorkers === 0 ? 'None' : supportWorkers}
                    </span>
                  </p>
                  {specialRequirements.trim() ? (
                    <p>
                      <span className="text-gray-500">Note</span>
                      <span className="block font-medium text-gray-900 whitespace-pre-wrap">
                        {specialRequirements.trim()}
                      </span>
                    </p>
                  ) : null}
                  <p>
                    <span className="text-gray-500">Payment</span>
                    <span className="block font-medium text-gray-900">
                      {bookingResult.payment_method === 'cash_bank' ? 'Cash or bank transfer' : 'Card'}
                    </span>
                  </p>
                  {fareBreakdown ? (
                    <p className="pt-2 border-t border-gray-200">
                      <span className="text-gray-500">Estimated total </span>
                      <span className="font-semibold text-gray-900">
                        {pricingService.formatCurrency(fareBreakdown.estimatedTotal)}
                      </span>
                    </p>
                  ) : null}
                </div>

                <div className="flex flex-col sm:flex-row gap-3 mb-6">
                  {bookingResult.access_token ? (
                    <a
                      href={`/booking-status?token=${bookingResult.access_token}`}
                      className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 text-center"
                    >
                      Track booking
                    </a>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => {
                      window.location.href = '/';
                    }}
                    className="flex-1 px-4 py-3 border border-gray-300 text-gray-800 rounded-lg text-sm font-medium hover:bg-gray-50"
                  >
                    Home
                  </button>
                </div>

                <p className="text-xs text-gray-500">
                  We’ll follow up by email if we need anything else.
                </p>

                <p className="text-xs text-gray-500 mt-4">
                  <Link to="/signup" className="text-blue-600 hover:underline">
                    Create an account
                  </Link>{' '}
                  to manage bookings later.
                </p>
              </>
            ) : (
              // Fallback for when bookingResult is null
              <div className="text-center">
                <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <AlertTriangle className="w-10 h-10 text-yellow-600" />
                </div>
                
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Booking Status Unavailable
                </h3>
                
                <p className="text-gray-600 mb-8">
                  We're having trouble loading your booking details. Please check your email for confirmation or contact support.
                </p>

                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={() => window.location.href = '/'}
                    className="flex-1 px-6 py-4 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors"
                  >
                    Return to Homepage
                  </button>
                  <button
                    onClick={() => setCurrentStep('booking')}
                    className="flex-1 px-6 py-4 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GuestBookingForm;